import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { isThisISOWeek } from 'date-fns/esm';
import { MedicineService } from 'src/core/services/medicine.service';
import { PermissionsService } from 'src/core/services/permissions.service';
import { toPackageTypeApiPayload } from 'src/core/utils/package-type-payload';
import { withSavedSageItemCode } from 'src/core/utils/sage-item-code';
import { dosageTypeSelectedValidator } from 'src/core/validators/dosage-type';
import { packageTypeSelectedValidator } from 'src/core/validators/package-type';
import { DosageType, DosageUnit, EditMedicineDosagePackageTypePayload, EditMedicineDosageTypePayload, MedicineDosagePackageType, MedicineDosageType, PackageType } from 'src/interfaces/medicine';

export interface DialogData {
  title: string;
  message: string;
  medicineDosageType: MedicineDosageType,
  medicineId: number;
  dosageTypes: DosageType[];
  packageTypes: PackageType[];
  dosageUnits: DosageUnit[];
}

@Component({
    selector: 'app-medicine-dosage-type-edit-dialog',
    templateUrl: './medicine-dosage-type-edit-dialog.component.html',
    styleUrls: ['./medicine-dosage-type-edit-dialog.component.scss'],
    standalone: false
})
export class MedicineDosageTypeEditDialogComponent implements OnInit {

  medicineDosageTypeForm = this._formBuilder.group({
    additionalInformation: ['', Validators.required],
    usageInstructions: ['', Validators.required],
    storageDirections: ['', Validators.required],
    specialInstructions: ['', Validators.required],
    dosageStrength: ['', Validators.required],
    dosageType: ['', dosageTypeSelectedValidator],
    dosageUnit: [''],
    isActive: ['', Validators.required]
  });

  medicineDosagePackageTypeForm = this._formBuilder.group({
    packageType: ['', packageTypeSelectedValidator],
    containedQuantity: ['', Validators.compose([Validators.required, Validators.min(1)])],
    unitsPerPack: [1, Validators.compose([Validators.required, Validators.min(1)])],
    minOrderQty: ['', Validators.min(1)],
    labelName: ["", Validators.compose([Validators.required, Validators.maxLength(25)])],
    price: ['', Validators.compose([Validators.required, Validators.min(1)])],
    sageItemCode: [''],
  })
  labelNameExceeds25Chars: boolean = false;

  success: boolean = false;
  dosageTypes: DosageType[];
  packageTypes: PackageType[];
  dosageUnits: DosageUnit[];
  currentDosageType: DosageType | null = null;
  currentDosageUnit: DosageUnit | null = null;
  medicineDosageType: MedicineDosageType | null = null;

  medicineDosagePackageTypes: MedicineDosagePackageType[] = [];

  editMode: boolean = false;

  valuesChanged: boolean = false;

  mainTabIndex = 0;
  packageTypesTabIndex = 0;

  get isManageView(): boolean {
    return this.mainTabIndex === 1
      || (this.mainTabIndex === 0 && this.packageTypesTabIndex === 1);
  }

  constructor(
    public _dialogRef: MatDialogRef<MedicineDosageTypeEditDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private _formBuilder: UntypedFormBuilder,
    private _medicineService: MedicineService,
    public readonly perm: PermissionsService,
  ) { }

  ngOnInit(): void {

    this.packageTypes = this.data.packageTypes;
    this.dosageTypes = this.data.dosageTypes;
    this.dosageUnits = this.data.dosageUnits;
    this.medicineDosageType = this.data.medicineDosageType;

    // Only patch form if medicineDosageType exists
    if (this.medicineDosageType) {
      const dosageType: DosageType | undefined = this.data.
        dosageTypes
        .find(type => {
          return type.id === this.medicineDosageType!.dosage_type_id
        });

      const dosageUnit: DosageUnit | undefined = dosageType ? this.dosageUnits
        .find(unit => unit.id === dosageType.dosage_unit_id) : undefined;

      this.currentDosageType = dosageType ? dosageType : null;
      this.currentDosageUnit = dosageUnit ? dosageUnit : null;

      this.medicineDosageTypeForm
        .patchValue({
          additionalInformation: this.medicineDosageType.additional_information || '',
          usageInstructions: this.medicineDosageType.usage_instructions || '',
          storageDirections: this.medicineDosageType.storage_directions || '',
          specialInstructions: this.medicineDosageType.special_instructions || '',
          dosageStrength: this.medicineDosageType.dosage_strength || '',
          dosageType: dosageType ? dosageType : null,
          dosageUnit: dosageUnit ? dosageUnit : null,
          isActive: this.medicineDosageType.is_active ? 'true' : 'false',
        });
    }

    // Listen for dosageType changes and automatically update dosageUnit
    this.medicineDosageTypeForm.get('dosageType')?.valueChanges
      .subscribe((selectedDosageType: DosageType | null) => {
        if (selectedDosageType) {
          const correspondingUnit = this.dosageUnits.find(
            unit => unit.id === selectedDosageType.dosage_unit_id
          );
          if (correspondingUnit) {
            this.medicineDosageTypeForm.patchValue({
              dosageUnit: correspondingUnit
            }, { emitEvent: false }); // Prevent infinite loop
          }
        }
      });

    this.medicineDosageTypeForm
      .valueChanges
      .subscribe(value => {
        this.valuesChanged = true;
      });

    this.medicineDosagePackageTypeForm
      .valueChanges
      .subscribe(value => {
        this.valuesChanged = true;
      });

    this.medicineDosagePackageTypeForm.controls['labelName']
      .valueChanges.subscribe((value) => {
        this.labelNameExceeds25Chars = value && value.length > 25;
      });

    // Load package types if medicineDosageType exists
    if (this.data.medicineDosageType) {
      (async () => {
        this.medicineDosagePackageTypes = await this._medicineService
          .getMedicineDosagePackageTypes(
            this.data.medicineId,
            this.data.medicineDosageType!.id,
            1, 10, ''
          )
      })();
    }
  }

  async save() {

    if (!this.medicineDosageType) return;

    const payload: EditMedicineDosageTypePayload = {
      additional_information: this.medicineDosageTypeForm.value.additionalInformation,
      usage_instructions: this.medicineDosageTypeForm.value.usageInstructions,
      storage_directions: this.medicineDosageTypeForm.value.storageDirections,
      special_instructions: this.medicineDosageTypeForm.value.specialInstructions,
      dosage_strength: this.medicineDosageTypeForm.value.dosageStrength,
      dosage_type_id: this.medicineDosageTypeForm.value.dosageType.id,
      is_active: this.medicineDosageTypeForm.value.isActive,
    }

    this.success = await this._medicineService
      .editMedicineDosageType(
        this.data.medicineId, this.medicineDosageType.id,
        payload);

    if (this.data.medicineDosageType) {

      this.currentDosageType = this.medicineDosageTypeForm.value.dosageType;

      const currentDosageUnit: DosageUnit | undefined = this.dosageUnits
        .find(dosageUnit => {
          if (!this.currentDosageType) return false;
          return dosageUnit.id === this.currentDosageType.dosage_unit_id
        });

      this.currentDosageUnit = currentDosageUnit ? currentDosageUnit : null;

    }

    this.valuesChanged = false;

  }

  async createMedicineDosagePackageType() {

    if (!this.data.medicineDosageType) return;

    const payload: EditMedicineDosagePackageTypePayload = toPackageTypeApiPayload(
      this.medicineDosagePackageTypeForm.getRawValue()
    );

    const medicineDosagePackageType: MedicineDosagePackageType | null = await this._medicineService
      .createMedicineDosagePackageType(
        this.data.medicineId,
        this.data.medicineDosageType.id,
        payload
      );

    if (medicineDosagePackageType && this.medicineDosageType) {
      const packs = await this._medicineService
        .getMedicineDosagePackageTypes(
          this.data.medicineId,
          this.medicineDosageType.id,
          1, 10, ''
        );
      this.medicineDosagePackageTypes = withSavedSageItemCode(
        packs,
        new Map([[medicineDosagePackageType.id, payload.sage_item_code]])
      );

      this.medicineDosagePackageTypeForm.reset({ unitsPerPack: 1, sageItemCode: '' });
      this.valuesChanged = false;
    }
  }

  async onDosagePackageTypesUpdated(updated: boolean) {
    if (updated && this.medicineDosageType) {
      this.medicineDosagePackageTypes = await this._medicineService
        .getMedicineDosagePackageTypes(
          this.data.medicineId,
          this.medicineDosageType.id,
          1, 10, ''
        );
    }
  }

  async createDosageUnit() {

    const newDosageUnit: DosageUnit = await this._medicineService
      .openDosageUnitCreateDialog(
        "New dosage unit",
        "Create a new dosage unit"
      );

    if (newDosageUnit) {
      // add dosage unit to the list dosage units 
      this.dosageUnits.push(newDosageUnit);
      // clone array 
      this.dosageUnits = [...this.dosageUnits];
      // Update the form value to the newly created dosage unit
      this.medicineDosageTypeForm.patchValue({
        dosageUnit: newDosageUnit
      });
    }
  }

  onDosageTypeCreated(newDosageType: DosageType) {
    this.dosageTypes.push(newDosageType);
    this.dosageTypes = [...this.dosageTypes];
  }

  onDosageTypesManaged(dosageTypes: DosageType[]) {
    this.dosageTypes = dosageTypes;
  }

  async deletePackageType(type: PackageType) {
    const success: boolean = await this._medicineService.deletePackageType(type.id);

    if (success)
      this.packageTypes = this.packageTypes
        .filter(packageType => packageType.id !== type.id);
  }

  onPackageTypeCreated(newPackageType: PackageType) {
    this.packageTypes.push(newPackageType);
    this.packageTypes = [...this.packageTypes];
  }

  closeDialog() {
    this._dialogRef.close(this.success);
  }

  getDosageType(medicineDosageType: MedicineDosageType) {
    const dosageType = this.dosageTypes.find(type => type.id === medicineDosageType.dosage_type_id);
    return dosageType ? dosageType.type : "";
  }

}
